import { useEffect, useMemo } from 'react';
import { Animated, Easing, StyleSheet, Text, View, type ViewStyle } from 'react-native';
import Svg, { Path } from 'react-native-svg';

import { colorSemantic, radii, spacing, useAppTheme } from '@/theme';

interface LoadingSpinnerProps {
  readonly color: string;
  readonly size?: number;
}

const LoadingSpinner = ({ color, size = 10 }: LoadingSpinnerProps) => {
  const rotation = useMemo(() => new Animated.Value(0), []);

  useEffect(() => {
    const animation = Animated.loop(
      Animated.timing(rotation, {
        toValue: 1,
        duration: 800,
        easing: Easing.linear,
        useNativeDriver: true,
      }),
    );
    animation.start();

    return () => animation.stop();
  }, [rotation]);

  const spin = useMemo(
    () =>
      rotation.interpolate({
        inputRange: [0, 1],
        outputRange: ['0deg', '360deg'],
      }),
    [rotation],
  );

  return (
    <Animated.View
      accessibilityLabel="status-badge-loading-indicator"
      accessibilityRole="progressbar"
      style={{ transform: [{ rotate: spin }] }}>
      <Svg fill="none" height={size} viewBox="0 0 10 10" width={size}>
        <Path
          d="M5.00004 0.833374C2.69879 0.833374 0.833374 2.69879 0.833374 5.00004C0.833374 7.30129 2.69879 9.16671 5.00004 9.16671C7.30129 9.16671 9.16671 7.30129 9.16671 5.00004H7.97623C7.97623 6.64124 6.64124 7.97623 5.00004 7.97623C3.35884 7.97623 2.02385 6.64124 2.02385 5.00004C2.02385 3.35884 3.35884 2.02385 5.00004 2.02385V0.833374Z"
          fill={color}
        />
      </Svg>
    </Animated.View>
  );
};

interface StatusBadgeProps {
  readonly label: string;
  readonly variant: 'info' | 'warning' | 'error' | 'success' | 'neutral';
  readonly loading?: boolean;
  readonly testID?: string;
  readonly style?: ViewStyle;
}

interface StatusBadgeColors {
  readonly backgroundColor: string;
  readonly textColor: string;
}

const getVariantColors = (
  variant: StatusBadgeProps['variant'],
  colors: ReturnType<typeof useAppTheme>['colors'],
): StatusBadgeColors => {
  if (variant === 'success') {
    return {
      backgroundColor: colors.secondaryContainer,
      textColor: colors.onSecondaryContainer,
    };
  }

  if (variant === 'info') {
    return {
      backgroundColor: colors.tertiaryContainer,
      textColor: colors.onTertiaryContainer,
    };
  }

  if (variant === 'warning') {
    return {
      backgroundColor: colors.warningContainer,
      textColor: colors.onWarningContainer,
    };
  }

  if (variant === 'neutral') {
    return {
      backgroundColor: colorSemantic.colorBgNlStronger,
      textColor: colorSemantic.colorTextNdPrimary,
    };
  }

  return {
    backgroundColor: colors.errorContainer,
    textColor: colors.onErrorContainer,
  };
};

export const StatusBadge = ({ label, variant, loading, testID, style }: StatusBadgeProps) => {
  const { colors: themeColors } = useAppTheme();
  const colors = getVariantColors(variant, themeColors);

  return (
    <View
      style={[styles.container, { backgroundColor: colors.backgroundColor }, style]}
      testID={testID}>
      {loading ? (
        <View style={styles.loadingIndicator}>
          <LoadingSpinner color={colors.textColor} />
        </View>
      ) : null}
      <Text style={[styles.label, { color: colors.textColor }]}>{label}</Text>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    borderRadius: radii.sm,
    overflow: 'hidden',
    paddingHorizontal: spacing[2],
    paddingVertical: 3,
  },
  loadingIndicator: {
    marginRight: spacing[1],
  },
  label: {
    fontSize: 11,
    fontWeight: '500',
    lineHeight: 16,
    letterSpacing: 0.1,
  },
});
